import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  { id: "name", label: "Your name", type: "text", placeholder: "Dr. Jane Doe" },
  {
    id: "clinic_name",
    label: "Clinic name",
    type: "text",
    placeholder: "Harley Street Aesthetics",
  },
  {
    id: "email",
    label: "Email",
    type: "email",
    placeholder: "you@clinic.com",
  },
  {
    id: "phone",
    label: "Phone",
    type: "tel",
    placeholder: "+44 20 7946 0000",
  },
];

const initial = {
  name: "",
  clinic_name: "",
  email: "",
  phone: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (id) => (e) => {
    setForm((f) => ({ ...f, [id]: e.target.value }));
    if (errors[id]) setErrors((er) => ({ ...er, [id]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Required";
    if (!form.clinic_name.trim()) er.clinic_name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) er.email = "Enter a valid email";
    if (form.phone.trim().length < 4) er.phone = "Required";
    if (form.message.trim().length < 5) er.message = "Tell us a bit more";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received", {
        description: "We'll be in touch within one business day.",
      });
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please retry.";
      toast.error("Couldn't send message", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left text */}
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-slate-500 font-bold mb-6">
              Contact
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white">
              Book a call.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-400 max-w-md">
              Share a few details and we'll come back within one business day
              to arrange a tailored ROI estimate, an integration plan and a
              live demo against your clinic's website and call flow.
            </p>

            <div className="mt-12 space-y-5 border-t border-white/10 pt-8">
              <a
                href="tel:+442036970069"
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 text-slate-400" />
                +44 (203) 697 0069
              </a>
              <a
                href="mailto:support@procliniq.co.uk"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 text-slate-400" />
                support@procliniq.co.uk
              </a>
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <MapPin className="w-4 h-4 text-slate-400" />
                London · United Kingdom
              </div>
            </div>
          </div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-slate-900 border border-white/10 rounded-sm p-8 sm:p-10"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {FIELDS.map((f) => (
                <div key={f.id} className={f.id === "name" ? "" : ""}>
                  <label
                    htmlFor={`field-${f.id}`}
                    className="block text-xs uppercase tracking-[0.18em] text-slate-400 font-bold mb-2"
                  >
                    {f.label}
                  </label>
                  <input
                    id={`field-${f.id}`}
                    data-testid={`contact-input-${f.id}`}
                    type={f.type}
                    placeholder={f.placeholder}
                    value={form[f.id]}
                    onChange={onChange(f.id)}
                    className={`bg-slate-950 border text-white placeholder:text-slate-600 focus:ring-1 focus:ring-white focus:border-white outline-none rounded-sm h-12 px-4 w-full transition-colors ${
                      errors[f.id] ? "border-red-500/60" : "border-white/10"
                    }`}
                  />
                  {errors[f.id] && (
                    <p
                      data-testid={`contact-error-${f.id}`}
                      className="mt-1.5 text-xs text-red-400"
                    >
                      {errors[f.id]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5">
              <label
                htmlFor="field-message"
                className="block text-xs uppercase tracking-[0.18em] text-slate-400 font-bold mb-2"
              >
                How can we help?
              </label>
              <textarea
                id="field-message"
                data-testid="contact-input-message"
                rows={5}
                placeholder="Tell us about your specialty, current call volume, and where leads are leaking."
                value={form.message}
                onChange={onChange("message")}
                className={`bg-slate-950 border text-white placeholder:text-slate-600 focus:ring-1 focus:ring-white focus:border-white outline-none rounded-sm py-3 px-4 w-full transition-colors resize-none ${
                  errors.message ? "border-red-500/60" : "border-white/10"
                }`}
              />
              {errors.message && (
                <p
                  data-testid="contact-error-message"
                  className="mt-1.5 text-xs text-red-400"
                >
                  {errors.message}
                </p>
              )}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-slate-500 max-w-sm">
                By submitting you agree to be contacted about ProCliniq's
                services. We don't share your details.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="inline-flex items-center justify-center gap-2 bg-white text-slate-950 hover:bg-slate-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors font-medium rounded-sm px-6 py-3.5 text-sm whitespace-nowrap"
              >
                {submitting ? "Sending…" : "Send message"}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
